import api, { setAccessToken, setRefreshToken, clearTokens } from './api';
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  User,
} from '@/types';

const AUTH_ENDPOINTS = {
  LOGIN: '/auth/login',
  REGISTER: '/auth/register',
  LOGOUT: '/auth/logout',
  ME: '/auth/me',
  REFRESH: '/auth/refresh',
};

// API login response structure (after interceptor unwraps { success, data })
interface ApiLoginData {
  user: User;
  tenant: {
    id: string;
    name: string;
    slug: string;
    status: string;
    plan: string;
  };
  tokens: {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: string;
  };
}

export const authService = {
  /**
   * Login user with email and password
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await api.post<ApiLoginData>(AUTH_ENDPOINTS.LOGIN, credentials);
      // API interceptor unwraps { success, data } - response.data is now the inner data
      const { user, tokens } = response.data;

      // Store tokens
      setAccessToken(tokens.access_token);
      setRefreshToken(tokens.refresh_token);

      return { user };
    } catch (error) {
      console.error('Auth service login error:', error);
      throw error;
    }
  },

  /**
   * Register a new user
   */
  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const response = await api.post<{ user: User }>(AUTH_ENDPOINTS.REGISTER, data);
    // API interceptor unwraps { success, data } - response.data is now the inner data
    const { user } = response.data;

    return { user };
  },

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await api.post(AUTH_ENDPOINTS.LOGOUT);
    } catch {
      // Even if the API call fails, clear local tokens
    } finally {
      clearTokens();
    }
  },

  /**
   * Get current user profile
   */
  async getMe(): Promise<User> {
    const response = await api.get<{ user: User }>(AUTH_ENDPOINTS.ME);
    // API interceptor unwraps { success, data } - response.data is now the inner data
    return response.data.user;
  },

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<{ accessToken: string }> {
    const response = await api.post<{ access_token: string }>(AUTH_ENDPOINTS.REFRESH, {
      refresh_token: refreshToken,
    });
    // API interceptor unwraps { success, data } - response.data is now the inner data
    setAccessToken(response.data.access_token);
    return { accessToken: response.data.access_token };
  },
};

export default authService;
